import { zodResolver } from '@hookform/resolvers/zod';
import { registerSchema } from '@willnq/shared/validation/authSchemas';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import type { z } from 'zod';

import { ROUTES } from '@/constants/routes';
import type { VerifyEmailState } from '@/features/identity/types/identity.types';
import { authService } from '@/services/authService';
import { parseRestError } from '@/utils/errorUtils';

type RegisterFormValues = z.infer<typeof registerSchema>;

/** Drives `RegisterForm` — on success, hands the email to `VerifyEmailPage` via router state. */
export function useRegisterForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);

  // The invitation landing page hands the invited address over so the account is created for the
  // exact email the invitation targets (acceptance enforces the match server-side).
  const prefilledEmail = (location.state as { email?: string } | null)?.email ?? '';

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      email: prefilledEmail,
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: '',
      // Never pre-checked: a default of true would produce an acceptance record for something the
      // person never actively agreed to (ADR-0026).
      termsAccepted: false,
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await authService.register({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone || undefined,
        // The API re-validates this and refuses the account without it — the checkbox is the
        // affordance, the server is the control (RegisterUserCommandValidator).
        termsAccepted: values.termsAccepted,
      });
      const state: VerifyEmailState = { email: values.email };
      navigate(ROUTES.verifyEmail, { state });
    } catch (error) {
      const { code, message } = parseRestError(error);
      if (code === 'Identity.EmailInUse') {
        form.setError('email', { message });
      } else {
        setServerError(message);
      }
    }
  });

  return { form, onSubmit, serverError };
}
